// New Project wizard — native, from-scratch project creation that drives project → budget → schedule.
// Step 1 creates the project (+ links the state's award); Step 2 hands off to the budget wizard and
// generates the BEAD schedule. Complements "Import from Ready" (award-driven) with a from-scratch path.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

const TECHS = [['fiber', 'Fiber'], ['fixed_wireless', 'Fixed Wireless'], ['hybrid', 'Hybrid']];
const num = (v: any) => Number(String(v ?? '').replace(/[$,\s]/g, '')) || 0;
const usd = (n: any) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

export default function NewProjectWizard({ onClose, onSetupBudget }: { onClose: () => void; onSetupBudget: (project: any) => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'form' | 'done'>('form');
  const [f, setF] = useState({ project_code: '', project_name: '', county: '', state: '', tech_type: 'fiber', grant_amount_usd: '', match_amount_usd: '', awarded_bsl_count: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [schedMsg, setSchedMsg] = useState('');

  const total = num(f.grant_amount_usd) + num(f.match_amount_usd);
  const set = (k: string, v: string) => setF(s => ({ ...s, [k]: v }));

  const create = async () => {
    setErr('');
    if (!f.project_code.trim()) { setErr('Project code is required.'); return; }
    setBusy(true);
    try {
      const r: any = await api.post('/bead/projects', {
        project_code: f.project_code.trim(), project_name: f.project_name.trim() || f.project_code.trim(),
        county: f.county.trim() || null, state: f.state.trim().toUpperCase() || null, tech_type: f.tech_type,
        grant_amount_usd: num(f.grant_amount_usd), match_amount_usd: num(f.match_amount_usd), total_project_cost_usd: total,
        awarded_bsl_count: f.awarded_bsl_count ? num(f.awarded_bsl_count) : null,
      });
      if (r?.ok) {
        const proj = { id: r.project_id, project_name: f.project_name.trim() || f.project_code.trim(), project_code: r.project_code, total_budget_usd: total };
        setCreated(proj); setStep('done');
        await qc.invalidateQueries({ queryKey: ['bead-projects'] });
      } else setErr(r?.message || r?.error || 'Failed to create project.');
    } catch (e: any) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  const genSchedule = async () => {
    if (!created) return;
    setSchedMsg('Generating…');
    try {
      const r: any = await api.post(`/bead/projects/${created.id}/schedule/generate`, startDate ? { start_date: startDate } : {});
      setSchedMsg(r?.generated ? `✓ ${r.generated}-activity schedule created (${r.project_start} → ${r.project_end})` : 'Done');
    } catch (e: any) { setSchedMsg('Failed: ' + (e?.message || e)); }
  };

  const field = (label: string, k: string, opts: { placeholder?: string; money?: boolean; wide?: boolean } = {}) => (
    <div style={{ gridColumn: opts.wide ? '1 / -1' : 'auto' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', marginBottom: 3 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px' }}>
        {opts.money && <span style={{ color: '#94a3b8', fontSize: 13 }}>$</span>}
        <input value={(f as any)[k]} onChange={e => set(k, e.target.value)} placeholder={opts.placeholder}
          style={{ width: '100%', border: 'none', outline: 'none', padding: '7px 4px', fontSize: 13, background: 'transparent' }} />
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg, #fff)', borderRadius: 12, width: 'min(640px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>New project</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Create a BEAD project from scratch, then set up its budget and schedule.</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ padding: 20, overflow: 'auto' }}>
          {step === 'form' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {field('Project code *', 'project_code', { placeholder: 'e.g. CM61-BEAD-XY-3-2001' })}
                {field('Project name', 'project_name', { placeholder: 'Defaults to the code' })}
                {field('County', 'county', { placeholder: 'e.g. Marion' })}
                {field('State', 'state', { placeholder: '2-letter, e.g. WV' })}
                <div>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', marginBottom: 3 }}>Technology</div>
                  <select value={f.tech_type} onChange={e => set('tech_type', e.target.value)} style={{ width: '100%', padding: '8px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)' }}>
                    {TECHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {field('Awarded BSLs', 'awarded_bsl_count', { placeholder: 'e.g. 597' })}
                {field('Grant (federal) funds', 'grant_amount_usd', { money: true, placeholder: '0' })}
                {field('Match funds', 'match_amount_usd', { money: true, placeholder: '0' })}
              </div>
              <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>Total project cost (grant + match)</span>
                <strong>{usd(total)}</strong>
              </div>
              {err && <div style={{ marginTop: 10, padding: 8, background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 12 }}>{err}</div>}
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button disabled={busy || !f.project_code.trim()} onClick={create}
                  style={{ padding: '9px 18px', background: (busy || !f.project_code.trim()) ? '#cbd5e1' : '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {busy ? 'Creating…' : 'Create project →'}
                </button>
              </div>
            </>
          )}

          {step === 'done' && created && (
            <>
              <div style={{ padding: 14, background: '#ecfdf5', color: '#065f46', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                ✓ Project <strong>{created.project_code}</strong> created. Now set up its budget and schedule.
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Budget</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Import your budget or enter it line-by-line, mapped to the SF-424A categories the state requires.</div>
                <button onClick={() => onSetupBudget(created)} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Set up budget →</button>
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Schedule</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Generate the standard 22-activity BEAD schedule (the state's fixed milestone set) from a start date.</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }} />
                  <button onClick={genSchedule} style={{ padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Generate schedule</button>
                  {schedMsg && <span style={{ fontSize: 11, color: schedMsg.startsWith('Fail') ? '#ef4444' : '#10b981' }}>{schedMsg}</span>}
                </div>
              </div>
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button onClick={onClose} style={{ padding: '9px 18px', background: '#0b1220', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
